import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

// Redux slices and actions
import { setSelectedUser, clearSelectedUser } from "src/slices/userSlice";

// Custom hooks
import { useUsersData } from "src/hooks/useUsersData";
import { useDialogBox } from "src/hooks/useDialogBox";

// Layout/UI
import { Box, Button, Text } from "@metrea-ui/v2";
import { LoadingIndicator } from "src/components/UI/common/LoadIndicator";
import { PageHeader } from "../SiteLayout/Header";
import { PageFooter } from "../SiteLayout/Footer";
import UsersList from "./UsersList";
import UserDetails from "./EditUser";

// Utilities
import { isTestEnv } from "src/utils/appUtils";
import { getIdFromPath } from "src/utils/dataUtils";
import { debugLog, errorLog } from "src/utils/debugUtils";
// Constants
import { initialUserState } from "src/constants/initialStates";

const Users = () => {
	const renderCount = useRef(0);
	renderCount.current++;

	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const { withError } = useDialogBox();

	// --- Redux Selectors ---
	const itemList = useSelector((state) => state.users.list); // All users
	const selectedUser = useSelector((state) => state.users.selectedUser); // The "current" (selected/edited) user
	const showList = useSelector((state) => !state.users.selectedUser); // Show list view when no user is selected

	// --- Redux Status/Error : For error and loading overlays ---
	const retrieveUsersStatus = useSelector((state) => state.users.status.retrieve);
	const retrieveUsersError = useSelector((state) => state.users.error.retrieve);
	const retrieveUserDetailError = useSelector((state) => state.users.error.retrieveOne);

	// --- Local State ---
	const [pageM1, setPageM1] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	// Custom hooks
	const { fetchUsers, fetchUserByIdOrUuid } = useUsersData();

	// --- Effect: initial data load
	useEffect(() => {
		debugLog(2, "[Users][FLOW] mounted", {});
		if (!isTestEnv && selectedUser) {
			dispatch(clearSelectedUser());
		}

		// Used for deep linking to an user by id/uuid in the URL
		const idFromPath = getIdFromPath(location.pathname);
		if (idFromPath) {
			debugLog(2, "[Users][FLOW] fetchUserByIdOrUuid started", { idOrUuid: idFromPath });
			fetchUserByIdOrUuid(idFromPath);
		} else {
			debugLog(2, "[Users][FLOW] fetchUsers started", {});
			fetchUsers();
		}

		debugLog(2, "[Users][FLOW] initial data load", {
			selectedBaseline: selectedUser,
			idFromPath: idFromPath,
		});
	}, [location]);

	// --- Effect: show Error dialog
	useEffect(() => {
		if (retrieveUsersError) {
			errorLog(2, "[Users][FLOW] retrieve users failed", { error: retrieveUsersError });
			withError(retrieveUsersError, () => fetchUsers(), {
				fallbackMessage: "Retrieve Users Error",
			});
		}
	}, [retrieveUsersError]);

	useEffect(() => {
		if (!retrieveUserDetailError) return;

		errorLog(2, "[Users][FLOW] retrieve user detail failed", { error: retrieveUserDetailError });
		const idFromPath = getIdFromPath(location.pathname);
		if (!idFromPath) return;

		const fallbackRoute = location.pathname.includes("/admin/user") ? "/admin/user" : "/user";
		// Only show detail-load errors while a detail route is active; once we
		// navigate back to the list, leave the stale retrieveOne error alone.
		withError(retrieveUserDetailError, () => fetchUserByIdOrUuid(idFromPath), {
			fallbackMessage: "Retrieve User Error",
			onClose: () => {
				navigate(fallbackRoute, { replace: true });
			},
		});
	}, [fetchUserByIdOrUuid, location.pathname, navigate, retrieveUserDetailError, withError]);

	useEffect(() => {
		const idFromPath = getIdFromPath(location.pathname);
		if (!idFromPath || !selectedUser?.id) return;
		if (!Number.isNaN(Number(idFromPath))) return;

		const baseRoute = location.pathname.includes("/admin/user") ? "/admin/user" : "/user";
		const normalizedPath = `${baseRoute}/${selectedUser.id}`;

		if (location.pathname !== normalizedPath) {
			// Replace UUID deep links with the standard numeric route after the detail load succeeds.
			navigate(normalizedPath, { replace: true });
		}
	}, [location.pathname, navigate, selectedUser?.id]);

	// Helper to clear the selected user (return to list)
	// const refreshData = () => {
	// 	dispatch(clearSelectedUser()); // instead of dispatch(setCurrentUser(null))
	// };

	// --- Handlers ---
	// Return to list view and reload users
	const handleReloadList = () => {
		debugLog(3, "[Users][ACTION] handleReloadList", {});
		dispatch(clearSelectedUser());
		debugLog(2, "[Users][FLOW] fetchUsers started", {});
		fetchUsers();
	};

	// Handler for selecting a user (opens the details/edit page)
	const selectUserHandler = (id) => {
		debugLog(3, "[Users][ACTION] selectUserHandler", { id: id });
		debugLog(2, "[Users][FLOW] fetchUserByIdOrUuid started", { idOrUuid: id });

		fetchUserByIdOrUuid(id);
	};

	// Handler for opening the create-user form
	const clickNewHandler = () => {
		debugLog(3, "[Users][ACTION] clickNewHandler", {});

		dispatch(setSelectedUser({ ...initialUserState }));
	};

	// Handlers for list pagination and rows-per-page
	const changePageM1Handler = (newPage) => {
		//To handle page change from Pagination (MetreaUI)
		setPageM1(newPage);
	};

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[Users][ACTION] changeRowsPerPageHandler", { option: option });

		setRowsPerPage(option);
		setPageM1(1);
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[Users][FLOW] render", {
		renderCount: renderCount.current,
		selectedUser: selectedUser,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<>
			{/* Header and "Add New" button */}
			<PageHeader pageTitle="User Management" actions={true}>
				{showList && (
					<>
						<Button style={{ padding: "10px" }} onClick={clickNewHandler}>
							Add New
						</Button>
					</>
				)}
			</PageHeader>

			<Box className="pageContent">
				{/* Overlay loading spinner if loading */}
				<LoadingIndicator open={retrieveUsersStatus === "loading"} />

				{/* Show empty state if list is loaded but empty */}
				{itemList.length === 0 && retrieveUsersStatus === "succeeded" && (
					<>
						<Text style={{ fontSize: "1.25rem", lineHeight: 1.4 }}>No users found.</Text>
					</>
				)}

				{/* Main content: Show UsersList or UserDetails depending on selection */}
				{showList ? (
					<UsersList
						items={itemList}
						pageM1={pageM1}
						setPageM1={changePageM1Handler}
						rowsPerPage={rowsPerPage}
						setRowsPerPage={changeRowsPerPageHandler}
						onSelectUser={selectUserHandler}
					/>
				) : (
					<UserDetails
						item={selectedUser}
						callFrom="User"
						mode="edit"
						onReloadList={handleReloadList}
					/>
				)}
				<PageFooter />
			</Box>
		</>
	);
};

export default Users;
